"use client";

import { Citrus, FlaskConical, Barrel, Wine } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  number: "01",
  title: "ASSESS",
  Icon: Citrus,
  description: "We analyze your technical specifications and project requirements. Every detail is reviewed to ensure absolute precision for your industrial labeling needs."
}, {
  number: "02",
  title: "PROGRAM",
  Icon: FlaskConical,
  description: "Our advanced CNC and laser systems are calibrated to your exact schematics. We translate complex data into high-fidelity, durable markings."
}, {
  number: "03",
  title: "ENGRAVE",
  Icon: Barrel,
  description: "Using industrial-grade materials, we execute deep-cut engraving or precision laser marking. We ensure every character meets rigorous quality standards."
}, {
  number: "04",
  title: "VERIFY",
  Icon: Wine,
  description: "Final inspection and quality assurance. Each component is verified for legibility, durability, and compliance before being packaged for delivery."
}];
export default function HowItWorks() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3883" id="how-it-works" className="bg-background text-foreground relative overflow-hidden py-32 md:py-[200px]">
      {}
      <div className="pointer-events-none absolute top-0 left-4 hidden h-full lg:left-8 lg:block">
        <div className="sticky top-32">
          <div className="font-sans text-[10px] font-medium tracking-[0.3em] uppercase text-foreground/50" style={{
          writingMode: "vertical-rl"
        }}>
            00 &mdash; OUR PROCESS
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-20">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 gap-10 md:grid-cols-12 md:gap-12 transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="md:col-span-7">
            <div className="mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-[var(--chart-1)]" />
              <span className="font-sans text-xs font-medium tracking-[0.25em] uppercase text-[var(--chart-1)]">
                Production Protocol
              </span>
            </div>
            <h2 className="font-default italic text-4xl leading-[1.05] tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-[64px]">
              Precision engineering
              <br />
              <span className="text-foreground/70">
                for industrial excellence.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5 md:pt-6">
            <p className="font-default text-lg leading-relaxed text-foreground/75 md:text-xl">
              At our facility, every project is handled with technical rigor. We specialize in high-durability identification solutions, ensuring your equipment remains compliant and clearly marked for years to come.
            </p>
            <p className="mt-5 font-default text-base leading-relaxed text-foreground/60">
              No errors. No delays. Only the exacting standards of modern manufacturing and precision laser technology.
            </p>
          </div>
        </div>

        {}
        <div className="relative mt-20 md:mt-28">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
            {steps.map((step, i) => {
            const card = useRevealOnScroll<HTMLDivElement>({
              threshold: 0.15
            });
            const Icon = step.Icon;
            return <div key={step.number} data-index={i} ref={card.ref} className={`group relative transition-all duration-700 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
              transitionDelay: `${i * 120}ms`
            }}>
                  {}
                  {i < steps.length - 1 && <div className="pointer-events-none absolute top-1/2 -right-3 z-20 hidden lg:block">
                      <div className="flex items-center gap-0.5">
                        <span className="h-px w-1.5 bg-[var(--chart-1)]" />
                        <span className="h-px w-1 bg-[var(--chart-1)]/60" />
                        <span className="h-px w-0.5 bg-[var(--chart-1)]/30" />
                      </div>
                    </div>}

                  {}
                  <div className="relative h-[380px] overflow-hidden border border-border bg-card backdrop-blur-xl shadow-lg transition-all duration-500 group-hover:-translate-y-1 group-hover:border-[var(--chart-1)] group-hover:shadow-2xl">
                    {}
                    <div className="absolute -top-4 -left-2 select-none">
                      <span className="font-sans text-[120px] font-extrabold leading-none tracking-tighter text-foreground/10 transition-colors duration-[400ms] group-hover:text-[var(--chart-1)]" aria-hidden="true">
                        {step.number}
                      </span>
                    </div>

                    {}
                    <div className="relative flex h-full flex-col justify-between p-8 pt-32">
                      <Icon className="h-10 w-10 text-foreground/80 transition-colors duration-300 group-hover:text-[var(--chart-1)]" strokeWidth={1} />

                      <div>
                        <div className="mb-4 h-px w-8 bg-foreground/30 transition-all duration-500 group-hover:w-16 group-hover:bg-[var(--chart-1)]" />
                        <h3 className="font-default font-sans text-2xl font-bold tracking-[0.15em] uppercase text-foreground">
                          {step.title}
                        </h3>
                        <p className="mt-4 font-default text-[15px] leading-relaxed text-foreground/65">
                          {step.description}
                        </p>
                      </div>
                    </div>

                    {}
                    <div className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--chart-1)] to-transparent" />
                    </div>
                  </div>
                </div>;
          })}
          </div>

          {}
          <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-border pt-8 sm:flex-row sm:items-center">
            <p className="font-sans text-xs tracking-[0.25em] uppercase text-foreground/50">
              Four steps &mdash; one standard
            </p>
            <p className="font-default italic text-sm text-foreground/60">
              Engineered for durability. Built for compliance.
            </p>
          </div>
        </div>
      </div>
    </section>;
}